import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import useTeamStore from '../../state/teamStore';

const PAGE_SIZE = 15;

export default function LocalTeamsList({ onHoverTeam }) {
  // Pull hydrated “Evaluate Teams” cache from Zustand
  const nearbyTeamsLocal = useTeamStore((s) => s.nearbyTeamsLocal) || [];
  const teamGeoOverrideLocal = useTeamStore((s) => s.teamGeoOverrideLocal) || [];
  const lastLocalCenter = useTeamStore((s) => s.lastLocalCenter);
  const lastLocalRadius = useTeamStore((s) => s.lastLocalRadius);
  // Derive current user id & focus (no store changes; read from common globals/localStorage)
  // Derive current user id & focus (robust: window globals, multiple persisted stores)
  const { myUserId, myFocus } = (() => {
    const out = { myUserId: null, myFocus: null };

    // Helper to normalize possible containers
    const pullUser = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      // common shapes in your app:
      // { u_id, u_customer_focus }
      if ('u_id' in obj || 'u_customer_focus' in obj) return obj;
      // nested shapes
      if (obj.user && (obj.user.u_id || obj.user.u_customer_focus)) return obj.user;
      if (obj.userData && (obj.userData.u_id || obj.userData.u_customer_focus)) return obj.userData;
      return null;
    };

    // 1) window globals
    const w = (typeof window !== 'undefined' ? window : {});
    const wUser = pullUser(w.__ehUser) || pullUser(w.__eh?.user) || null;
    if (wUser) {
      out.myUserId = Number(wUser.u_id) || out.myUserId;
      out.myFocus  = (wUser.u_customer_focus || '').toString().trim() || out.myFocus;
    }

    // 2) localStorage – common keys in your app
    const grabLS = (key) => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        const state = parsed?.state || parsed || null;
        const u = pullUser(state) || pullUser(state?.user) || state?.user || null;
        if (u) {
          out.myUserId = out.myUserId ?? (Number(u.u_id) || null);
          out.myFocus  = out.myFocus  ?? ((u.u_customer_focus || '').toString().trim() || null);
        }
      } catch {}
    };
    grabLS('eh_team_v1');
    grabLS('eh_user_v1');
    grabLS('eh_profile_v1');

    // 3) sessionStorage fallback (seen in some screens)
    try {
      const raw = sessionStorage.getItem('eh_user_v1');
      if (raw && (!out.myUserId || !out.myFocus)) {
        const parsed = JSON.parse(raw);
        const state = parsed?.state || parsed || null;
        const u = pullUser(state) || pullUser(state?.user) || state?.user || null;
        if (u) {
          out.myUserId = out.myUserId ?? (Number(u.u_id) || null);
          out.myFocus  = out.myFocus  ?? ((u.u_customer_focus || '').toString().trim() || null);
        }
      }
    } catch {}

    // normalize casing
    if (out.myFocus) out.myFocus = out.myFocus.toUpperCase();

    return out;
  })();


  // Column-3 anchoring (kept for parity with LocalUsersTeamList; useful for a modal later)
  const containerRef = useRef(null);
  const [topOffset, setTopOffset] = useState(0);
  const [rightOffset, setRightOffset] = useState(0);

  const computeOffsets = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTopOffset(Math.round(rect.top + window.scrollY));
    setRightOffset(Math.round(window.innerWidth - rect.right));
  };

  useLayoutEffect(() => {
    computeOffsets();
    window.addEventListener('resize', computeOffsets);
    return () => window.removeEventListener('resize', computeOffsets);
  }, []);

  // Geo overrides → Map
  const geoMap = useMemo(() => {
    const m = new Map();
    if (Array.isArray(teamGeoOverrideLocal)) {
      for (const entry of teamGeoOverrideLocal) {
        if (Array.isArray(entry) && entry.length === 2) {
          m.set(Number(entry[0]), entry[1]);
        }
      }
    }
    return m;
  }, [teamGeoOverrideLocal]);

  // Build display rows (sorted by distance, then alpha)
  const rows = useMemo(() => {
    const center = lastLocalCenter;
    const withGeo = nearbyTeamsLocal.map((t) => {
      const id = Number(t?.team_id || t?.id);
      const pt = geoMap.get(id);

      let lat = t?.b_lat ?? t?.lat;
      let lng = t?.b_lng ?? t?.lng;
      if (pt && pt.lat != null && pt.lng != null) {
        lat = pt.lat; lng = pt.lng;
      }

      const miles = (center && Number.isFinite(lat) && Number.isFinite(lng))
        ? haversineMiles(center.lat, center.lng, +lat, +lng)
        : null;

        // Captain photo (accept several shapes; match users list behavior)
        const pic =
        t?.captain_profile_picture ??
        t?.captain_u_profile_picture ??
        t?.u_profile_picture ??
        t?.captain_photo ??
        t?.photo_url ??
        '';

        const avatar = pic
        ? (String(pic).startsWith('http') ? String(pic) : `${process.env.REACT_APP_API_URL || ''}${pic}`)
        : '/default-avatar.png';

        // Captain focus (for badge + filtering) — force UPPERCASE for reliable compares
        const focus = String(
        t?.captain_customer_focus ??
        t?.u_customer_focus ??
        t?.b_customer_focus ??
        t?.customer_focus ??
        ''
        ).trim().toUpperCase();



      return {
        id,
        team_name: (t?.team_name || '').toString().trim(),
        b_name: (t?.b_name || '').toString().trim(),
        b_city: (t?.b_city || '').toString().trim(),
        b_state: (t?.b_state || '').toString().trim(),
        b_zip_code: (t?.b_zip_code || '').toString().replace(/\D/g, '').slice(0, 5),
        miles,
        avatar,         // ← NEW (captain photo like users list)
        focus,
        raw: t,
      };

    });
    // --- filters ---
// --- filters ---
// One-time debug to verify inputs used for filtering
if (process.env.NODE_ENV !== 'production' && !window.__eh_joinTeamsLogged) {
  window.__eh_joinTeamsLogged = true;
  const sample = withGeo[0]?.raw || {};
  console.log('[JoinTeams] me=', { myUserId, myFocus },
              'sampleRowFocus:',
              (sample.captain_customer_focus ?? sample.u_customer_focus ?? sample.b_customer_focus ?? sample.customer_focus ?? null));
}

    const filtered = withGeo.filter((r) => {
    // Exclude my own team (captain/owner matches me)
    if (myUserId && Number(r?.raw?.owner_u_id) === Number(myUserId)) return false;

    // Focus filter (show only teams whose captain focus matches mine)
    if (myFocus) {
        const tf = (r.focus || '').toUpperCase();
        if (tf !== myFocus) return false;   // <-- both sides uppercase now
    }
    return true;
    });

    if (process.env.NODE_ENV !== 'production') {
    console.log('[JoinTeams] rows before:', withGeo.length, 'after:', filtered.length);
    }

    return filtered.sort((a, b) => {
    const am = a.miles ?? Number.POSITIVE_INFINITY;
    const bm = b.miles ?? Number.POSITIVE_INFINITY;
    if (am !== bm) return am - bm;
    return a.team_name.localeCompare(b.team_name, undefined, { sensitivity: 'base' });
    });


  }, [nearbyTeamsLocal, geoMap, lastLocalCenter]);

  // Paging (identical behavior to LocalUsersTeamList)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setVisibleCount(Math.min(PAGE_SIZE, rows.length || 0));
    setLoadingMore(false);
  }, [rows.length]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (loadingMore) return;
        if (visibleCount >= rows.length) return;

        setLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((n) => Math.min(n + PAGE_SIZE, rows.length));
          setLoadingMore(false);
        }, 300);
      },
      {
        root: containerRef.current, // scroll container
        threshold: 0.5,
      }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [visibleCount, rows.length, loadingMore]);

  const hasTeams = rows.length > 0;

  return (
    <div
      ref={containerRef}
      className="
        w-full min-w-0 box-border bg-white rounded-lg p-0
        shadow-[0_0_20px_rgba(0,0,150,0.1)]
        h-[550px] overflow-y-scroll pr-2 relative
        scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent
      "
    >
      {/* Sticky header (match users list; title text changed) */}
      <div className="sticky top-0 z-10 bg-white px-4 py-4 border-b">
        <h2 className="text-xl font-bold text-[#00aef7] text-center">Join Teams</h2>
        {Number.isFinite(lastLocalRadius) && lastLocalCenter && (
          <div className="mt-1 text-xs text-gray-500 text-center">

          </div>
        )}
      </div>

      <div className="px-4 py-4">
        {!hasTeams ? (
          <div className="bg-gray-50 border border-[#00aef7]/10 rounded-xl shadow-[0_4px_14px_rgba(0,174,247,0.08)] p-4 text-sm text-gray-700">
            No local teams found yet.
          </div>
        ) : (
          <>
            <ul className="space-y-3">
              {rows.slice(0, visibleCount).map((t, idx) => (
                <li key={t.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      // Match TeamInfo map-pin behavior
                      window.dispatchEvent(new CustomEvent('team:openModal', { detail: t.raw }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      window.dispatchEvent(new CustomEvent('team:openModal', { detail: t.raw }));
                    }}
                    onMouseEnter={() => onHoverTeam && onHoverTeam(t.id)}
                    onMouseLeave={() => onHoverTeam && onHoverTeam(null)}
                    className="w-full text-left"
                  >

                    <div
                      className="
                        p-3 bg-gray-50 rounded-xl
                        border border-[#00aef7]/10
                        shadow-[0_4px_14px_rgba(0,174,247,0.08)]
                        hover:shadow-[0_6px_20px_rgba(0,174,247,0.12)]
                        transition-shadow
                      "
                    >
                      <div className="flex items-start gap-3">
                        {/* Left column: avatar placeholder + TEAM badge for visual parity */}
                        <div className="flex flex-col items-center">
                            <div className="flex flex-col items-center">
                            <img
                                src={t.avatar}
                                alt={`${t.team_name || 'Team'} Captain`}
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                            />
                            {t.focus && (
                            <span
                                className="mt-1 inline-flex items-center px-1.5 py-[2px] text-[10px] font-bold rounded-full
                                        bg-blue-50 text-[#00aef7] border border-[#00aef7]/20"
                                title="Customer focus"
                            >
                                {t.focus.toUpperCase()}
                            </span>
                            )}

                            </div>


                        </div>

                        {/* Right column: text content */}
                        <div className="flex-1 min-w-0">
                          {/* Row 1: Team name */}
                          <div className="text-sm font-semibold text-gray-900 break-words">
                            {t.team_name || 'Team'}
                          </div>

                          {/* Row 2: Business name */}


                          {/* Row 3: City/State/Zip */}
                          <div className="text-xs text-gray-500 mt-0.5 break-words">
                            {[t.b_city, t.b_state].filter(Boolean).join(', ')}
                            {t.b_zip_code ? ` ${t.b_zip_code}` : ''}
                          </div>

                          {/* Bottom-right: distance */}
                          <div className="mt-2 text-xs text-gray-600">
                            {t.miles != null ? `${t.miles.toFixed(1)} mi` : '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const sentinelIndex = Math.min(visibleCount, rows.length) - 6; // 10th item of current page
                    return idx === sentinelIndex && visibleCount < rows.length ? (
                      <div ref={sentinelRef} className="h-1" />
                    ) : null;
                  })()}
                </li>
              ))}
            </ul>

            {loadingMore && (
              <div className="py-3 text-center text-xs text-gray-500">Loading more…</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function haversineMiles(lat1, lng1, lat2, lng2) {
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c) / 1609.34;
}
